// src/Pages/EditCommitment.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Form, Button, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../Context/AuthContext";
import Sample14 from "../Components/Logo";

import { getCommitmentById, updateCommitment } from "../Api/IncomingPo";
import { fetchBanks } from "../Api/ViewBank";

const EditCommitment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const resolvedUserId =
    user?.userId ?? user?.UserId ?? user?.id ?? Number(localStorage.getItem("userId")) ?? 0;

  // form fields (editable per your UpdateRequest / controller)
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("");
  const [typeOfCommitment, setTypeOfCommitment] = useState(""); // maps to dto.Type
  const [paymentsBankName, setPaymentsBankName] = useState(""); // backend stores name

  // banks dropdown: code+name => we will send NAME
  const [banks, setBanks] = useState([]); // [{code, name}]
  const [bankCode, setBankCode] = useState(""); // selection value (code)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const ready = useMemo(() => Boolean(id), [id]);

  const freqOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

  const typeOptions = [
    { value: "online subscription", label: "Online Subscription" },
    { value: "monthly subscription", label: "Monthly Subscription" },
  ];

  // load banks
  const loadBanks = async (orgId) => {
    const res = await fetchBanks(orgId);
    const arr = Array.isArray(res) ? res : res?.data || [];
    const normalized = arr
      .map((r) => ({
        code: r?.code ?? r?.bankCode ?? r?.BankCode ?? "",
        name: r?.description ?? r?.bankName ?? r?.BankName ?? "",
      }))
      .filter((x) => x.code && x.name);
    setBanks(normalized);
    return normalized;
  };

  // load commitment + banks, then map bank name -> code
  useEffect(() => {
    if (!ready) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const orgId = user?.organisationId ?? user?.orgId ?? user?.OrgId ?? null;
        const [commitment, bankList] = await Promise.all([
          getCommitmentById(id),
          loadBanks(orgId),
        ]);

        const c = Array.isArray(commitment) ? commitment[0] : commitment || {};
        const desc = c?.description ?? c?.Description ?? "";
        const amt = c?.amount ?? c?.Amount ?? 0;
        const freq = (c?.frequency ?? c?.Frequency ?? "").toLowerCase();
        const bankName = c?.paymentsBank ?? c?.PaymentsBank ?? "";
        const typ = c?.type ?? c?.Type ?? "";

        if (cancelled) return;

        setDescription(desc);
        setAmount(String(amt));
        setFrequency(freq);
        setTypeOfCommitment(typ);
        setPaymentsBankName(bankName);

        // find bank code matching stored name
        const match = bankList.find((b) => String(b.name).toLowerCase() === String(bankName).toLowerCase());
        if (match) setBankCode(match.code);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message ?? e?.message ?? "Failed to load commitment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Update name when user changes dropdown code
  useEffect(() => {
    if (!bankCode) return;
    const b = banks.find((x) => String(x.code) === String(bankCode));
    setPaymentsBankName(b?.name ?? "");
  }, [bankCode, banks]);

  const validate = () => {
    const amt = Number(amount);
    if (!description.trim()) { toast.error("Description is required."); return false; }
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Amount must be > 0"); return false; }
    if (!frequency) { toast.error("Frequency is required."); return false; }
    if (!typeOfCommitment) { toast.error("Type is required."); return false; }
    if (!paymentsBankName) { toast.error("Payments Bank is required."); return false; }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      description: description.trim(),     // your DTO includes Description (controller currently ignores it)
      amount: Number(amount),
      userId: Number(resolvedUserId),
      frequency,
      paymentsBank: paymentsBankName,      // send NAME (backend expects string)
      type: typeOfCommitment,              // maps to dto.Type
    };

    setSaving(true);
    try {
      await updateCommitment(id, payload);
      toast.success("Commitment updated");
      navigate("/viewcommitment");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message ?? e?.message ?? "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!ready || loading) {
    return (
      <div className="main-content expand">
        <div className="loading-overlay"><Sample14 /></div>
      </div>
    );
  }

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="fields-title m-0">Edit Commitment</h2>
          <Button variant="outline-secondary" onClick={() => navigate("/viewcommitment")}>
            Back
          </Button>
        </div>

        <Form onSubmit={onSubmit} className="fields-form">
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Description <span className="text-red">**</span></Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Amount <span className="text-red">**</span></Form.Label>
                <InputGroup>
                  <InputGroup.Text>₹</InputGroup.Text>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Frequency <span className="text-red">**</span></Form.Label>
                <Form.Select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  required
                >
                  <option value="">Select Frequency</option>
                  {["monthly","quarterly","yearly"].map(v => (
                    <option key={v} value={v}>{v[0].toUpperCase()+v.slice(1)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Type <span className="text-red">**</span></Form.Label>
                <Form.Select
                  value={typeOfCommitment}
                  onChange={(e) => setTypeOfCommitment(e.target.value)}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="online subscription">Online Subscription</option>
                  <option value="monthly subscription">Monthly Subscription</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Payments Bank <span className="text-red">**</span></Form.Label>
                <Form.Select
                  value={bankCode}
                  onChange={(e) => setBankCode(e.target.value)}
                  required
                >
                  <option value="">Select Bank</option>
                  {banks.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.code}{b.name ? ` - ${b.name}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="fields-btn-container">
            <Button
              variant="secondary"
              type="button"
              className="me-2"
              onClick={() => navigate("/viewcommitment")}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Update Commitment"}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default EditCommitment;
